import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';

const STORAGE_KEY = 'amden-cookie-notice-ack';

// Lightweight cookie notice. The public website uses only strictly necessary
// cookies (see /cookie-policy), so this is an honest notice — not a fake
// consent manager with toggles for cookies that do not exist.
export const CookieNotice = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (!window.localStorage.getItem(STORAGE_KEY)) setVisible(true);
    } catch {
      setVisible(true); // storage unavailable — show rather than never inform
    }
  }, []);

  const dismiss = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    } catch {
      /* storage unavailable — notice simply reappears next visit */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="region"
      aria-label="Cookie notice"
      data-testid="cookie-notice"
      className="fixed inset-x-0 bottom-0 z-40 px-4 pb-4 sm:px-6"
    >
      <div className="mx-auto flex max-w-3xl flex-col gap-4 rounded-xl border border-white/10 bg-navy-900 p-5 shadow-lift sm:flex-row sm:items-center">
        <div className="flex items-start gap-3">
          <Cookie className="mt-0.5 h-5 w-5 shrink-0 text-gold-500" aria-hidden="true" />
          <p className="text-sm leading-relaxed text-slate-200">
            This website uses only strictly necessary cookies needed for it to work — no tracking or
            advertising cookies.{' '}
            <Link
              to="/cookie-policy"
              data-testid="cookie-notice-policy"
              className="font-bold text-gold-500 underline-offset-2 hover:underline"
            >
              Read the Cookie Policy
            </Link>
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          data-testid="cookie-notice-accept"
          className="inline-flex shrink-0 items-center justify-center rounded-md bg-gold-500 px-5 py-2.5 text-sm font-bold text-navy-950 transition-colors hover:bg-gold-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-900"
        >
          Got it
        </button>
      </div>
    </div>
  );
};
